import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-explicit-confirmation',
  templateUrl: './explicit-confirmation.component.html',
  styleUrls: ['./explicit-confirmation.component.css'],
})
export class ExplicitConfirmationComponent {
  explicitTextInput: any; //holds user's input value

  @Input() explicitConfirmationText: string = 'CONFIRMATION_TEXT';
  @Input() title: string = 'CONFIRMATION_TITLE';
  @Input() messages: IExplicitConfirmationMessage[] = [
    { message: 'CONFIRMATION_MESSAGE', icon: 'warning', color: 'warn' },
  ];
  @Input() cancelButtonText: string = 'Dialog.Cancel';
  @Input() confirmButtonText: string = 'CONFIRM_BUTTON_TEXT';
  @Input() inputLabelPrefix: string = 'Dialog.ExplicityConfirmationLabelPrefix';
  @Input() inputLabelSuffix: string = 'Dialog.ExplicityConfirmationLabelSuffix';

  /**
   * Whether the strings handed to this dialog are translation keys.
   *
   * True for every caller that passes keys, which is the original contract and stays the default.
   * Callers on pages that are deliberately not localised pass false: their text is already the
   * text, and running it through the pipe turns it into `[bracketed placeholders]` via
   * CommonMissingTranslationHandler.
   *
   * The flag exists because the two confirmation dialogs disagree — ConfirmationDialogComponent
   * translates nothing at all — and nothing at a call site reveals which convention applies.
   */
  @Input() translateContent: boolean = true;
  private _width: string = '55%';
  get width(): string {
    return this._width;
  }
  @Input() set width(value: string) {
    this._width = value;
    this.dialogRef.updateSize(value, 'auto');
  }
  @Output() confirm = new EventEmitter<boolean>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      explicitConfirmationText?: string;
      title?: string;
      messages: any[];
      cancelButtonText?: string;
      confirmButtonText?: string;
      inputLabelPrefix?: string;
      inputLabelSuffix?: string;
      width?: string;
      translateContent?: boolean;
    },
    private dialogRef: MatDialogRef<ExplicitConfirmationComponent, boolean>,
    private translate: TranslateService,
  ) {
    this.dialogRef.disableClose = true; //ensure the user has to click close

    // Read before anything else: it decides how every other value is treated.
    this.translateContent = data?.translateContent ?? this.translateContent;

    const confirmationText = data?.explicitConfirmationText || this.explicitConfirmationText;
    this.explicitConfirmationText = this.translateContent
      ? this.translate.instant(confirmationText)
      : confirmationText;

    this.title = data?.title ?? this.title;
    this.messages = data?.messages ?? this.messages;
    this.cancelButtonText = data?.cancelButtonText ?? this.cancelButtonText;
    this.confirmButtonText = data?.confirmButtonText ?? this.confirmButtonText;
    this.inputLabelSuffix = data?.inputLabelSuffix ?? this.inputLabelSuffix;
    this.inputLabelPrefix = data?.inputLabelPrefix ?? this.inputLabelPrefix;
    this.width = data?.width ?? this.width;
  }

  onConfirmButtonClick() {
    this.dialogRef?.close(true);
    this.confirm.emit(true);
  }

  onCancelButtonClick() {
    this.dialogRef?.close(false);
    this.confirm.emit(false);
  }
}

export interface IExplicitConfirmationMessage {
  message: string;
  icon: string;
  color: string;
}
